// src/context/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from "react";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userPlan, setUserPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // ✅ Load from sessionStorage (auto cleared on tab close)
  useEffect(() => {
    try {
      const storedUser = sessionStorage.getItem("userInfo");
      const storedPlan = sessionStorage.getItem("userPlan");

      if (storedUser) setUser(JSON.parse(storedUser));
      if (storedPlan) setUserPlan(JSON.parse(storedPlan));
    } catch (err) {
      console.error("Error parsing session data:", err);
      sessionStorage.clear();
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ✅ Login
  const login = (userData, planData = null) => {
    try {
      setUser(userData);
      sessionStorage.setItem("userInfo", JSON.stringify(userData));

      if (planData) {
        setUserPlan(planData);
        sessionStorage.setItem("userPlan", JSON.stringify(planData));
      }
    } catch (error) {
      console.error("Login storage error:", error);
    }
  };

  // ✅ Update Plan
  const updateUserPlan = (planData) => {
    try {
      setUserPlan(planData);

      if (planData) {
        sessionStorage.setItem("userPlan", JSON.stringify(planData));
      } else {
        sessionStorage.removeItem("userPlan");
      }
    } catch (error) {
      console.error("Plan update error:", error);
    }
  };

  // ✅ Logout
  const logout = () => {
    try {
      setUser(null);
      setUserPlan(null);
      sessionStorage.clear(); // 🔥 important
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  // ✅ Check auth
  const isAuthenticated = () => {
    return !!sessionStorage.getItem("userInfo");
  };

  // ✅ Safe getter
  const getUserPlan = () => {
    if (userPlan) return userPlan;

    try {
      const storedPlan = sessionStorage.getItem("userPlan");
      return storedPlan ? JSON.parse(storedPlan) : null;
    } catch {
      return null;
    }
  };


  const contextValue = {
    user,
    userPlan,
    setUserPlan: updateUserPlan,
    login,
    logout,
    getUserPlan,
    isAuthenticated,
    isLoading,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};


export const useAuth = () => useContext(AuthContext);
